/* Attire Dot Raj2 gallery zoom — vanilla JS, no third-party dependencies.
   Designed to coexist with the existing thumb/swipe/variant gallery controller. */
(() => {
  'use strict';

  if (window.RDRaj2Zoom?.installed) {
    window.RDRaj2Zoom.scan();
    return;
  }

  const initialized = new WeakSet();
  const coarse = () => matchMedia('(pointer: coarse) and (max-width: 749px)').matches;
  const desktopHover = () => matchMedia('(hover: hover) and (pointer: fine)').matches;
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  let activeViewer = null;

  function getPhotos(root) {
    const thumbs = Array.from(root.querySelectorAll('[data-raj2-thumb]'));
    return Array.from(root.querySelectorAll('[data-raj2-media]'))
      .filter(slide => !!slide.dataset.rdZoomSrc)
      .map(slide => {
        const mediaId = slide.dataset.mediaId;
        const thumb = thumbs.find(item => item.dataset.mediaId === mediaId);
        const inlineImage = slide.querySelector('img.hero-product-raj2__main-image');
        const thumbImage = thumb?.querySelector('img');
        return {
          mediaId,
          src: slide.dataset.rdZoomSrc,
          preview: inlineImage?.currentSrc || inlineImage?.src || '',
          thumb: thumbImage?.src || inlineImage?.src || '',
          alt: inlineImage?.alt || thumbImage?.alt || 'Product image'
        };
      });
  }

  class Lightbox {
    constructor(root, photos, index) {
      this.root = root;
      this.photos = photos;
      this.index = index;
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers = new Map();
      this.gesture = null;
      this.loadId = 0;
      this.previousOverflow = document.documentElement.style.overflow;
      this.previousBodyOverflow = document.body.style.overflow;

      this.dialog = document.createElement('dialog');
      this.dialog.className = 'rd-raj2-zoom-dialog';
      this.dialog.setAttribute('aria-label', 'Product image viewer');
      this.dialog.innerHTML = `
        <div class="rd-raj2-zoom-layout">
          <div class="rd-raj2-zoom-topbar">
            <span class="rd-raj2-zoom-counter" aria-live="polite"></span>
            <div class="rd-raj2-zoom-tools">
              <button type="button" class="rd-raj2-zoom-tool" data-rd-zoom-minus aria-label="Zoom out">−</button>
              <span class="rd-raj2-zoom-percentage" aria-live="off">100%</span>
              <button type="button" class="rd-raj2-zoom-tool" data-rd-zoom-plus aria-label="Zoom in">+</button>
              <button type="button" class="rd-raj2-zoom-tool" data-rd-zoom-fit aria-label="Fit image to screen" style="font-size:12px;min-width:41px">Fit</button>
              <button type="button" class="rd-raj2-zoom-tool rd-raj2-zoom-close" data-rd-zoom-close aria-label="Close viewer">×</button>
            </div>
          </div>
          <div class="rd-raj2-zoom-main">
            <div class="rd-raj2-zoom-stage" aria-label="Drag to explore image; tap to zoom">
              <img class="rd-raj2-zoom-photo" alt="" draggable="false">
              <div class="rd-raj2-zoom-hint">Tap to zoom · Drag to explore</div>
            </div>
            <button class="rd-raj2-zoom-nav rd-raj2-zoom-prev" type="button" aria-label="Previous image">‹</button>
            <button class="rd-raj2-zoom-nav rd-raj2-zoom-next" type="button" aria-label="Next image">›</button>
          </div>
          <div class="rd-raj2-zoom-foot"><div class="rd-raj2-zoom-thumbs" role="group" aria-label="Product image thumbnails"></div></div>
        </div>`;
      this.stage = this.dialog.querySelector('.rd-raj2-zoom-stage');
      this.photo = this.dialog.querySelector('.rd-raj2-zoom-photo');
      this.counter = this.dialog.querySelector('.rd-raj2-zoom-counter');
      this.percent = this.dialog.querySelector('.rd-raj2-zoom-percentage');
      this.thumbs = this.dialog.querySelector('.rd-raj2-zoom-thumbs');
      this.previous = this.dialog.querySelector('.rd-raj2-zoom-prev');
      this.next = this.dialog.querySelector('.rd-raj2-zoom-next');
      this.minus = this.dialog.querySelector('[data-rd-zoom-minus]');
      this.plus = this.dialog.querySelector('[data-rd-zoom-plus]');
      this.hint = this.dialog.querySelector('.rd-raj2-zoom-hint');
      this.previous.hidden = photos.length < 2;
      this.next.hidden = photos.length < 2;

      photos.forEach((photo, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'rd-raj2-zoom-thumb';
        b.setAttribute('aria-label', `View image ${i + 1}`);
        const img = document.createElement('img');
        img.src = photo.thumb || photo.preview || photo.src;
        img.alt = '';
        img.loading = 'lazy';
        b.append(img);
        b.addEventListener('click', () => this.show(i));
        this.thumbs.append(b);
      });

      this.dialog.querySelector('[data-rd-zoom-close]').addEventListener('click', () => this.close());
      this.dialog.querySelector('[data-rd-zoom-fit]').addEventListener('click', () => this.setZoom(1));
      this.minus.addEventListener('click', () => this.setZoom(this.scale - 0.5));
      this.plus.addEventListener('click', () => this.setZoom(this.scale + 0.5));
      this.previous.addEventListener('click', () => this.show((this.index - 1 + photos.length) % photos.length));
      this.next.addEventListener('click', () => this.show((this.index + 1) % photos.length));
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('close', () => this.cleanup());
      this.dialog.addEventListener('keydown', event => this.handleKeyDown(event));
      this.stage.addEventListener('pointerdown', event => this.onPointerDown(event));
      this.stage.addEventListener('pointermove', event => this.onPointerMove(event));
      this.stage.addEventListener('pointerup', event => this.onPointerUp(event));
      this.stage.addEventListener('pointercancel', event => this.onPointerCancel(event));
      this.stage.addEventListener('lostpointercapture', event => this.onPointerCancel(event));
      this.stage.addEventListener('wheel', event => {
        event.preventDefault();
        const rect = this.stage.getBoundingClientRect();
        const point = { x: event.clientX - rect.left - rect.width / 2, y: event.clientY - rect.top - rect.height / 2 };
        this.setZoom(this.scale * (event.deltaY > 0 ? 0.88 : 1.12), point);
      }, { passive: false });
      this.photo.addEventListener('load', () => this.render());
      this.onResize = () => this.render();
      window.addEventListener('resize', this.onResize, { passive: true });

      document.body.append(this.dialog);
      try {
        this.dialog.showModal();
      } catch (error) {
        this.dialog.setAttribute('open', ''); // Old browsers fallback
      }
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      this.show(index, true);
      this.dialog.querySelector('[data-rd-zoom-close]').focus({ preventScroll: true });
    }

    show(index, first = false) {
      this.index = (index + this.photos.length) % this.photos.length;
      const photo = this.photos[this.index];
      const loadId = ++this.loadId;
      this.pointers.clear();
      this.gesture = null;
      this.scale = (first && coarse()) ? 2 : 1;
      this.tx = 0;
      this.ty = 0;
      this.photo.alt = photo.alt;
      this.photo.src = photo.preview || photo.src;
      this.counter.textContent = `${this.index + 1} / ${this.photos.length}`;
      this.thumbs.querySelectorAll('button').forEach((button, i) => {
        button.setAttribute('aria-current', String(i === this.index));
        if (i === this.index) button.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      });
      this.render();

      // Lazy high-resolution fetch: opening the product must not download every 3000px image.
      if (this.photo.src !== photo.src) {
        const highRes = new Image();
        highRes.decoding = 'async';
        highRes.onload = () => { if (loadId === this.loadId) this.photo.src = photo.src; };
        highRes.src = photo.src;
      }
    }

    limits() {
      const width = this.stage.clientWidth || 1;
      const height = this.stage.clientHeight || 1;
      const iw = this.photo.naturalWidth || width;
      const ih = this.photo.naturalHeight || height;
      const ratio = Math.min(width / iw, height / ih);
      const actualW = iw * ratio;
      const actualH = ih * ratio;
      return {
        x: Math.max(0, (actualW * this.scale - width) / 2),
        y: Math.max(0, (actualH * this.scale - height) / 2)
      };
    }

    render() {
      const max = this.limits();
      this.tx = clamp(this.tx, -max.x, max.x);
      this.ty = clamp(this.ty, -max.y, max.y);
      this.photo.style.transform = `translate(-50%, -50%) translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
      this.percent.textContent = `${Math.round(this.scale * 100)}%`;
      this.plus.disabled = this.scale >= 5;
      this.minus.disabled = this.scale <= 1;
      this.stage.classList.toggle('is-zoomed', this.scale > 1);
      this.hint.textContent = this.scale <= 1 ? 'Tap to zoom' : 'Drag to explore · Pinch to zoom';
    }

    setZoom(nextScale, point = { x: 0, y: 0 }) {
      const old = this.scale;
      this.scale = clamp(nextScale, 1, 5);
      if (this.scale <= 1) {
        this.tx = this.ty = 0;
      } else {
        const factor = this.scale / old;
        this.tx = point.x - (point.x - this.tx) * factor;
        this.ty = point.y - (point.y - this.ty) * factor;
      }
      this.render();
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      this.stage.setPointerCapture?.(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 1) {
        this.gesture = { x: event.clientX, y: event.clientY, moved: false, multi: false, dragX: 0, dragY: 0 };
      } else if (this.gesture) {
        this.gesture.multi = true;
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      const old = this.pointers.get(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (!this.gesture) return;
      if (this.pointers.size === 2) {
        const entries = [...this.pointers.entries()];
        const other = entries.find(([id]) => id !== event.pointerId)?.[1];
        if (other) {
          const oldDist = Math.hypot(old.x - other.x, old.y - other.y);
          const newDist = Math.hypot(event.clientX - other.x, event.clientY - other.y);
          const rect = this.stage.getBoundingClientRect();
          const midpoint = {
            x: (event.clientX + other.x) / 2 - rect.left - rect.width / 2,
            y: (event.clientY + other.y) / 2 - rect.top - rect.height / 2
          };
          if (oldDist > 0 && newDist > 0) this.setZoom(this.scale * newDist / oldDist, midpoint);
        }
        this.gesture.moved = true;
        this.gesture.multi = true;
      } else if (this.pointers.size === 1) {
        const dx = event.clientX - old.x;
        const dy = event.clientY - old.y;
        if (Math.hypot(event.clientX - this.gesture.x, event.clientY - this.gesture.y) > 8) this.gesture.moved = true;
        this.gesture.dragX += dx;
        this.gesture.dragY += dy;
        if (this.scale > 1) {
          this.tx += dx;
          this.ty += dy;
          this.stage.classList.add('is-panning');
          this.render();
        }
      }
    }

    onPointerUp(event) {
      if (!this.pointers.has(event.pointerId)) return;
      const start = this.gesture;
      this.pointers.delete(event.pointerId);
      if (this.pointers.size) { if (start) start.multi = true; return; }
      this.stage.classList.remove('is-panning');
      this.gesture = null;
      if (!start || start.multi) return;
      if (!start.moved) {
        const rect = this.stage.getBoundingClientRect();
        const point = { x: event.clientX - rect.left - rect.width / 2, y: event.clientY - rect.top - rect.height / 2 };
        this.setZoom(this.scale > 1 ? 1 : 2.5, point);
      } else if (this.scale === 1 && Math.abs(start.dragX) > 65 && Math.abs(start.dragX) > Math.abs(start.dragY) * 1.3 && this.photos.length > 1) {
        this.show((this.index + (start.dragX < 0 ? 1 : -1) + this.photos.length) % this.photos.length);
      }
    }

    onPointerCancel(event) {
      this.pointers.delete(event.pointerId);
      if (!this.pointers.size) {
        this.stage.classList.remove('is-panning');
        this.gesture = null;
      } else if (this.gesture) {
        this.gesture.multi = true;
      }
    }

    handleKeyDown(event) {
      const key = event.key;
      if (key === 'ArrowRight' && this.photos.length > 1) { event.preventDefault(); this.next.click(); }
      else if (key === 'ArrowLeft' && this.photos.length > 1) { event.preventDefault(); this.previous.click(); }
      else if (key === '+' || key === '=') { event.preventDefault(); this.setZoom(this.scale + .5); }
      else if (key === '-' || key === '_') { event.preventDefault(); this.setZoom(this.scale - .5); }
      else if (key === '0') { event.preventDefault(); this.setZoom(1); }
    }

    close() {
      if (this.dialog.open && typeof this.dialog.close === 'function') this.dialog.close();
      else this.cleanup();
    }

    cleanup() {
      if (this.cleaned) return;
      this.cleaned = true;
      this.loadId++;
      window.removeEventListener('resize', this.onResize);
      document.documentElement.style.overflow = this.previousOverflow;
      document.body.style.overflow = this.previousBodyOverflow;
      const selected = this.photos[this.index];
      const originalTrack = this.root.querySelector('[data-raj2-main-track]');
      const originalSlide = Array.from(this.root.querySelectorAll('[data-raj2-media]'))[
        Number(originalTrack?.dataset.activeIndex || 0)
      ];
      if (selected && originalSlide?.dataset.mediaId !== selected.mediaId) {
        const targetThumb = Array.from(this.root.querySelectorAll('[data-raj2-thumb]'))
          .find(thumb => thumb.dataset.mediaId === selected.mediaId);
        targetThumb?.click(); // Retain variant/gallery controller's existing manual slide state.
      }
      this.dialog.remove();
      if (activeViewer === this) activeViewer = null;
      this.root.querySelector('[data-raj2-main-media]')?.focus({ preventScroll: true });
    }
  }

  function openLightbox(root) {
    if (activeViewer) return;
    const photos = getPhotos(root);
    if (!photos.length) return;
    const track = root.querySelector('[data-raj2-main-track]');
    const slide = Array.from(root.querySelectorAll('[data-raj2-media]'))[Number(track?.dataset.activeIndex || 0)];
    const current = photos.findIndex(photo => photo.mediaId === slide?.dataset.mediaId);
    if (current < 0) return; // Do not hijack video / 3D-model controls.
    activeViewer = new Lightbox(root, photos, current);
  }

  function init(root) {
    if (initialized.has(root)) return;
    const main = root.querySelector('[data-raj2-main-media]');
    if (!main) return;
    initialized.add(root);
    main.tabIndex = 0;
    main.setAttribute('aria-label', 'Open product image viewer');

    let hoverFrame = 0;
    let latestEvent = null;
    let down = null;
    let cancelClickBefore = 0;
    let touchOpenedAt = 0;

    function zoomable() {
      const idx = Number(root.querySelector('[data-raj2-main-track]')?.dataset.activeIndex || 0);
      return !!Array.from(root.querySelectorAll('[data-raj2-media]'))[idx]?.dataset.rdZoomSrc;
    }
    function stopHover() { main.classList.remove('is-rd-zoom-hover'); }
    function updateHover(event) {
      if (!desktopHover() || !zoomable() || main.classList.contains('is-dragging') || (event.buttons || 0) !== 0) {
        stopHover(); return;
      }
      const rect = main.getBoundingClientRect();
      latestEvent = { x: (event.clientX - rect.left) / rect.width * 100, y: (event.clientY - rect.top) / rect.height * 100 };
      if (hoverFrame) return;
      hoverFrame = requestAnimationFrame(() => {
        hoverFrame = 0;
        if (!latestEvent) return;
        main.style.setProperty('--rd-zoom-origin-x', `${clamp(latestEvent.x, 0, 100)}%`);
        main.style.setProperty('--rd-zoom-origin-y', `${clamp(latestEvent.y, 0, 100)}%`);
        main.classList.add('is-rd-zoom-hover');
      });
    }

    main.addEventListener('pointermove', event => {
      updateHover(event);
      if (down && event.pointerId === down.id) {
        if (Math.hypot(event.clientX - down.x, event.clientY - down.y) > 11) {
          down.moved = true;
          cancelClickBefore = Date.now() + 500;
        }
      }
    }, true);
    main.addEventListener('pointerleave', stopHover);
    main.addEventListener('pointerdown', event => {
      stopHover();
      if (event.target.closest('button, a, video, iframe, model-viewer')) return;
      if (event.button !== undefined && event.button !== 0) return;
      if (down && event.pointerId !== down.id) { down.moved = true; cancelClickBefore = Date.now() + 500; return; }
      down = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false, type: event.pointerType };
    }, true);
    main.addEventListener('pointerup', event => {
      if (!down || event.pointerId !== down.id) return;
      const moved = down.moved || Math.hypot(event.clientX - down.x, event.clientY - down.y) > 11;
      const type = down.type;
      down = null;
      if (moved) { cancelClickBefore = Date.now() + 500; return; }
      if (type === 'touch' && zoomable()) {
        touchOpenedAt = Date.now();
        requestAnimationFrame(() => openLightbox(root)); // Run after the carousel releases pointer capture (iOS safe).
      }
    }, true);
    main.addEventListener('pointercancel', () => { down = null; cancelClickBefore = Date.now() + 500; stopHover(); }, true);
    main.addEventListener('click', event => {
      if (event.target.closest('button, a, video, iframe, model-viewer')) return;
      if (Date.now() < cancelClickBefore || Date.now() - touchOpenedAt < 600) return;
      if (!zoomable()) return;
      event.preventDefault();
      openLightbox(root);
    }, true);
    main.addEventListener('keydown', event => {
      if (event.target !== main || !zoomable()) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openLightbox(root);
      }
    });
    root.addEventListener('click', event => {
      if (event.target.closest('[data-raj2-thumb], [data-raj2-thumb-next], [data-raj2-thumb-prev]')) stopHover();
    });
  }

  function scan(scope = document) {
    scope.querySelectorAll('[data-raj2-product-hero]').forEach(init);
    if (scope.matches?.('[data-raj2-product-hero]')) init(scope);
  }

  window.RDRaj2Zoom = { installed: true, scan };
  scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (activeViewer && event.target.contains(activeViewer.root)) activeViewer.close();
  });
})();
